/**
 * Stabilize Plotly histogram2d color scale (z) and optional axis extents.
 * Prevents colorbar / heatmap intensity from jumping between renders when Plotly
 * re-autoscales z or tick "nice" ranges shift slightly.
 */

function toFiniteNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function binCount(n) {
  const v = Math.floor(Number(n));
  return v >= 1 ? v : 20;
}

/**
 * Max cell count for paired (x,y) points using the same equal-width bin grid as Studio:
 * nx = binCount(nbX), ny = binCount(nbY), span / count = bin size.
 */
export function computeHistogram2dStableZmaxFromPairs(pairs, nbX, nbY) {
  const bx = binCount(nbX);
  const by = binCount(nbY);
  const xs = [];
  const ys = [];
  for (const p of pairs || []) {
    const x = toFiniteNumber(p.x);
    const y = toFiniteNumber(p.y);
    if (x != null && y != null) {
      xs.push(x);
      ys.push(y);
    }
  }
  if (!xs.length) return 1;
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;
  const xSize = xSpan > 0 ? xSpan / bx : 1;
  const ySize = ySpan > 0 ? ySpan / by : 1;
  const counts = new Map();
  for (let i = 0; i < xs.length; i++) {
    let ix = Math.floor((xs[i] - xMin) / xSize);
    let iy = Math.floor((ys[i] - yMin) / ySize);
    if (ix < 0) ix = 0;
    if (iy < 0) iy = 0;
    if (ix >= bx) ix = bx - 1;
    if (iy >= by) iy = by - 1;
    const k = ix * by + iy;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  let max = 0;
  for (const c of counts.values()) max = Math.max(max, c);
  return Math.max(1, max);
}

/**
 * Set zmin / zmax / zauto on every histogram2d trace from the union of all points
 * (stable color scale across multi-trace / legend groups).
 */
export function applyHistogram2dStableColorScale(traces, nbX, nbY) {
  if (!Array.isArray(traces) || !traces.length) return traces;
  if (!traces.some((t) => t && t.type === "histogram2d")) return traces;
  const pairs = [];
  for (const t of traces) {
    if (!t || t.type !== "histogram2d" || !Array.isArray(t.x) || !Array.isArray(t.y)) continue;
    const n = Math.min(t.x.length, t.y.length);
    for (let i = 0; i < n; i++) {
      pairs.push({ x: t.x[i], y: t.y[i] });
    }
  }
  const zmax = computeHistogram2dStableZmaxFromPairs(pairs, nbX, nbY);
  traces.forEach((t) => {
    if (t && t.type === "histogram2d") {
      t.zmin = 0;
      t.zmax = zmax;
      t.zauto = false;
    }
  });
  return traces;
}

/** Axis data extents for fixed linear range (1% padding each side). */
export function getHistogram2dExtentsFromAxisArrays(xAxisData, yAxisDataRow) {
  if (!Array.isArray(xAxisData) || !Array.isArray(yAxisDataRow)) return null;
  const n = Math.min(xAxisData.length, yAxisDataRow.length);
  const xs = [];
  const ys = [];
  for (let i = 0; i < n; i++) {
    const x = toFiniteNumber(xAxisData[i]);
    const y = toFiniteNumber(yAxisDataRow[i]);
    if (x != null && y != null) {
      xs.push(x);
      ys.push(y);
    }
  }
  if (!xs.length) return null;
  return {
    xmin: Math.min(...xs),
    xmax: Math.max(...xs),
    ymin: Math.min(...ys),
    ymax: Math.max(...ys),
  };
}

/** Numeric extents from table rows (agentic / dashboard card). */
export function getHistogram2dExtentsFromTableRows(rows, xCol, yCol) {
  if (!Array.isArray(rows) || !xCol || !yCol) return null;
  const xs = [];
  const ys = [];
  for (const row of rows) {
    if (!row) continue;
    const x = toFiniteNumber(row[xCol]);
    const y = toFiniteNumber(row[yCol]);
    if (x != null && y != null) {
      xs.push(x);
      ys.push(y);
    }
  }
  if (!xs.length) return null;
  return {
    xmin: Math.min(...xs),
    xmax: Math.max(...xs),
    ymin: Math.min(...ys),
    ymax: Math.max(...ys),
  };
}

export function padHistogram2dAxisRange(ext, padFraction = 0.01) {
  if (!ext) return null;
  const xSpan = ext.xmax - ext.xmin;
  const ySpan = ext.ymax - ext.ymin;
  const xPad = Math.max(xSpan * padFraction, 1e-9 * (Math.abs(ext.xmax) + 1));
  const yPad = Math.max(ySpan * padFraction, 1e-9 * (Math.abs(ext.ymax) + 1));
  return {
    xrange: [ext.xmin - xPad, ext.xmax + xPad],
    yrange: [ext.ymin - yPad, ext.ymax + yPad],
  };
}

export const HISTOGRAM2D_BLUES_SCALE = [
  [0.0, "rgba(247,251,255,255)"],
  [0.25, "rgba(193,217,237,255)"],
  [0.5, "rgba(97,167,210,255)"],
  [0.75, "rgba(24,101,172,255)"],
  [1.0, "rgba(8,48,108,255)"],
];

/**
 * Turn a scatter-style trace into histogram2d bins + styling (Studio-aligned).
 */
export function styleTraceAsHistogram2d(trace, nbX, nbY) {
  if (!trace || !Array.isArray(trace.x) || !Array.isArray(trace.y)) return trace;
  const bx = binCount(nbX);
  const by = binCount(nbY);
  const pairs = [];
  const n = Math.min(trace.x.length, trace.y.length);
  for (let i = 0; i < n; i++) {
    pairs.push({ x: trace.x[i], y: trace.y[i] });
  }
  const xs = [];
  const ys = [];
  for (const p of pairs) {
    const x = toFiniteNumber(p.x);
    const y = toFiniteNumber(p.y);
    if (x != null && y != null) {
      xs.push(x);
      ys.push(y);
    }
  }
  if (!xs.length) return trace;

  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;
  const xSize = xSpan > 0 ? xSpan / bx : 1;
  const ySize = ySpan > 0 ? ySpan / by : 1;

  delete trace.mode;
  delete trace.marker;
  delete trace.line;

  return Object.assign(trace, {
    type: "histogram2d",
    autobinx: false,
    autobiny: false,
    xbins: { start: xMin, end: xMax, size: xSize },
    ybins: { start: yMin, end: yMax, size: ySize },
    colorscale: HISTOGRAM2D_BLUES_SCALE,
    colorbar: {
      title: { text: "Number of Samples", side: "right" },
    },
  });
}
